import {TransactionEntryLine} from '@/types/transaction/TransactionEntryLine'
import _, {groupBy, sumBy, values} from 'lodash'
import {format} from 'date-fns'
import {EntryLineValidationResult, EntryLineValidator, ErrorCode} from '@/transaction/create-transaction/EntryLineValidationResult'
import {isDateBeforeOrEqual} from '@/services/formatter.service'
import {suppliers} from '@/subaccount/subAccount.service'
import {periods, periodsLoaded} from '@/period-lock/periodLock.service'
import {KidGenerator} from '@/bank/KidGenerator'
import {useSetting} from '@/setting/setting.service'
import {validateBban as bbanValidator} from '@/bank/bank-account.service'


export const floatsAreEqual = (numberOne: number, numberTwo: number, threshold: number = 0.01) => {
    return Math.abs(numberOne - numberTwo) < threshold
}

const {setting: projectRequired} = useSetting<boolean | null>('PROJECT_REQUIRED')
const {setting: departmentRequired} = useSetting<boolean | null>('DEPARTMENT_REQUIRED')
const {setting: descriptionRequired} = useSetting<boolean | null>('DESCRIPTION_REQUIRED')


const validateDepartment = (entryLines: TransactionEntryLine[]) => {
    if (!departmentRequired.value.value) {
        return {
            valid: true,
            errors: []
        }
    }
    const valid = entryLines.every((entryLine) => {
        return !!entryLine.department
    })

    return {
        valid,
        errors: valid ? [] : [ErrorCode.NO_DEPARTMENT]
    }
}

const validateProject = (entryLines: TransactionEntryLine[]) => {
    if (!projectRequired.value.value) {
        return {
            valid: true,
            errors: []
        }
    }
    const valid = entryLines.every((entryLine) => {
        return !!entryLine.project
    })

    return {
        valid,
        errors: valid ? [] : [ErrorCode.NO_PROJECT]
    }
}

const validateDescription = (entryLines: TransactionEntryLine[]) => {
    if (!descriptionRequired.value.value) {
        return {
            valid: true,
            errors: []
        }
    }
    const valid = entryLines.every((entryLine) => {
        return !!entryLine.description
    })

    return {
        valid,
        errors: valid ? [] : [ErrorCode.NO_DESCRIPTION]
    }
}

const validateKID = (entryLines: TransactionEntryLine[]): EntryLineValidationResult => {
    const valid = entryLines.every(line => !line.kid || KidGenerator.isValid(line.kid) )
    return {
        valid,
        errors: valid ? [] : [ErrorCode.INVALID_KID]
    }
}

const validateBban = (entryLines: TransactionEntryLine[]) => {
    const valid = entryLines.every(line => {
        if (line.bban) {
            return bbanValidator(line.bban)
        } else {
            return true
        }
    })
    return {
        valid,
        errors: valid ? [] : [ErrorCode.INVALID_BANK_ACCOUNT]
    }
}

const validateBankAccount = (entryLines: TransactionEntryLine[]) => {
    const valid = entryLines.every(line => {
        if (line.invoiceNo && line.creditSubAccount) {
            const supplier = suppliers.value.find(s => s.uuid === line.creditSubAccount)
            if (!supplier) return true
            if (!supplier.iban && !supplier.bban) return true
            return supplier.iban === line.bban || supplier.bban === line.bban
        }
        return true
    })
    return {
        valid,
        errors: valid ? [] : [ErrorCode.INVALID_BANK_ACCOUNT]
    }
}

const validateLineCount: EntryLineValidator = (entryLines: TransactionEntryLine[]) => {
    const valid = entryLines.length > 0

    return {
        valid,
        errors: valid ? [] : [ErrorCode.NO_ENTRY_LINES]
    }
}

const validateAccrual = (entryLines: TransactionEntryLine[]) => {
    // TODO: fiks accrual validation rules (now we have start month, start year and accrual info on each line)
    // problem is when e.g. we have two cost lines with accrual and one credit line without accrual
    let valid = entryLines.every(el =>
        !(el.accrualAccountNo || el.accrualDuration) ||
        (el.accrualAccountNo && el.accrualDuration && el.debitAccount && el.debitAccount >= '3000' && el.creditAccount === '2400' && el.creditSubAccount !== undefined)
    )

    valid = true
    return {
        valid,
        errors: valid ? [] : [ErrorCode.INVALID_ACCRUAL]
    }

}

const validateLineAmounts = (entryLines: TransactionEntryLine[]) => {
    const valid = entryLines.every((entryLine) => _.isNumber(entryLine.amount) && entryLine.amount > 0)
    return {
        valid,
        errors: valid ? [] : [ErrorCode.NO_AMOUNT]
    }
}

const validateLineAccounts = (entryLines: TransactionEntryLine[]) => {
    const valid = entryLines.every((entryLine) => entryLine.debitAccount || entryLine.creditAccount)

    return {
        valid,
        errors: valid ? [] : [ErrorCode.NO_ACCOUNT]
    }
}

const validateYearBalance = (entryLines: TransactionEntryLine[]) => {

    const entriesByYear = groupBy(entryLines, (entryLine) => format(entryLine.date, 'yyyy'))

    const valid = values(entriesByYear).every((entries) => {
        const debitSum = sumBy(entries, (entry) => entry.debitAccount ? entry.amount : 0)
        const creditSum = sumBy(entries, (entry) => entry.creditAccount ? entry.amount : 0)
        return floatsAreEqual(debitSum, creditSum)
    })

    return {
        valid,
        errors: valid ? [] : [ErrorCode.UNBALANCED]
    }
}

const validateOnlyOneSubAccountPerEntryLine = (entryLines: TransactionEntryLine[]) => {

    const valid = entryLines.every((entryLine) => {
        return !entryLine.debitSubAccount || !entryLine.creditSubAccount
    })

    return {
        valid,
        errors: valid ? [] : [ErrorCode.TOO_MANY_SUB_ACCOUNTS]
    }
}

const validateCorrectAccount = (entryLines: TransactionEntryLine[]) => {
    const validAccounts = ['1500', '2400']

    const valid = entryLines.every((entryLine) => {
        let lineValid = true
        if (entryLine.debitSubAccount)
            lineValid = lineValid && validAccounts.includes(entryLine.debitAccount!)
        if (entryLine.creditSubAccount)
            lineValid = lineValid && validAccounts.includes(entryLine.creditAccount!)
        return lineValid
    })

    return {
        valid,
        errors: valid ? [] : [ErrorCode.WRONG_ACCOUNT]
    }
}

const validateSubAccountSetWhenMasterAccountSet = (entryLines: TransactionEntryLine[]) => {
    const masterAccounts = ['1500', '2400']

    const valid = entryLines.every((entryLine) => {
        let lineValid = true
        if (masterAccounts.includes(entryLine.debitAccount!))
            lineValid = lineValid && !!entryLine.debitSubAccount
        if (masterAccounts.includes(entryLine.creditAccount!))
            lineValid = lineValid && !!entryLine.creditSubAccount
        return lineValid
    })

    return {
        valid,
        errors: valid ? [] : [ErrorCode.WRONG_ACCOUNT]
    }
}

// eslint-disable-next-line
const validateCorrectInvoiceDueDate = (entryLines: TransactionEntryLine[]) => {
    const valid = entryLines.every((entryLine) => {
        let lineValid = true
        if (entryLine.invoiceDueDate) {
            lineValid = isDateBeforeOrEqual(entryLine.date, entryLine.invoiceDueDate)
        }
        return lineValid
    })

    return {
        valid,
        errors: valid ? [] : [ErrorCode.WRONG_INVOICE_DUE_DATE]
    }
}

const validateDate = (entryLines: TransactionEntryLine[]) => {
    const valid = entryLines.every((entryLine) => {
        let lineValid = true
        if (entryLine.date) {
            const currentDate = new Date()
            const nextYear = currentDate.getFullYear() + 1
            const endOfNextYear = new Date(nextYear, 11, 31)
            lineValid = isDateBeforeOrEqual(entryLine.date, endOfNextYear)
        }
        return lineValid
    })

    return {
        valid,
        errors: valid ? [] : [ErrorCode.INVALID_DATE]
    }
}

const validatePeriodOpen = (entryLines: TransactionEntryLine[]) => {
    if (!periodsLoaded.value) {
        return {
            valid: true,
            errors: []
        }
    }
    const valid = entryLines.every((entryLine) => {
        if (!entryLine.date) return false
        const entryYear = entryLine.date.getFullYear()
        const entryMonth = entryLine.date.getMonth() + 1
        const period = periods.value.find(p => p.year === entryYear && p.month === entryMonth)
        return period ? !period.isClosed : true
    })

    return {
        valid,
        errors: valid ? [] : [ErrorCode.PERIOD_CLOSED]
    }
}

const validators: EntryLineValidator[] = [
    validateLineCount,
    validateLineAccounts,
    validateLineAmounts,
    validateYearBalance,
    validateOnlyOneSubAccountPerEntryLine,
    validateCorrectAccount,
    validateSubAccountSetWhenMasterAccountSet,
    // validateCorrectInvoiceDueDate, TODO disabled due to https://gitlab.com/tritt/accounting/pracsys/-/issues/861, enable when smart update transaction implemented
    validateAccrual,
    validateDate,
    validateBankAccount,
    validateKID,
    validatePeriodOpen,
    validateDepartment,
    validateProject,
    validateDescription,
    validateBban
]

export const validateEntryLines: EntryLineValidator = (entryLines: TransactionEntryLine[]) => {

    let valid = true
    const errors: ErrorCode[] = []
    validators.forEach(validator => {
        const result = validator(entryLines)
        valid = valid && result.valid
        if (!result.valid)
            errors.push(...result.errors)
    })

    return {
        valid,
        errors
    }
}